import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@rocco/utils/cn';
import { useExistImageBoost } from '@modules/page-builder/hooks/use-exist-image-boost';
import { toast } from 'sonner';
import type { ComponentPropsWithoutRef } from 'react';
import type * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { BoostButton } from './BoostButton';
import { BoostLoadingOverlay } from './BoostLoadingOverlay';
import { ImageComparisonCurtain } from './ImageComparisonCurtain';

/** Hides hover action buttons while an inline video player shows a play error overlay. */
const hoverActionBarClassName = cn(
    'pointer-events-none absolute right-3 bottom-3 z-10 flex items-center gap-3',
    'opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100',
    'group-has-[[data-play-error=true]]:pointer-events-none group-has-[[data-play-error=true]]:opacity-0!',
);

interface BoostResolutionOverlayProps {
    /** The LibraryImage ID */
    libraryImageId: number;
    /** Current image URL */
    currentImageUrl: string;
    /** Display width of the image container */
    displayWidth: number;
    /** Display height of the image container */
    displayHeight: number;
    /** Expected width passed to the boost API — defaults to displayWidth */
    expectedWidth?: number;
    /** Expected height passed to the boost API — defaults to displayHeight */
    expectedHeight?: number;
    /** Optional ImageColumn ID — pass for page builder module images so the column reference is updated */
    columnId?: string | number;
    /** Callback when new image is kept */
    onKeepNewImage?: () => void;
    /** Callback when image is reverted */
    onRevert?: () => void;
    /** Whether the image has already been upscaled */
    wasUpscaled?: boolean;
    /** Children (the actual image element) */
    children: React.ReactNode;
    /** Optional className for the wrapper */
    className?: string;
    /** Optional className to override the action bar position (defaults to bottom-right) */
    buttonClassName?: string;
    /** Additional actions to render alongside the boost button (e.g. download icon) */
    actionSlot?: React.ReactNode;
    /** Override className for the "Before" label in the comparison curtain */
    beforeClassName?: string;
    /** Override className for the "After" label in the comparison curtain */
    afterClassName?: string;
    /** Override className for the action buttons container (Keep/Revert/Close) in the comparison curtain */
    actionsClassName?: string;
    /** Whether the image uses multiply blend mode — forwarded to the comparison curtain */
    multiplied?: boolean;
    /** Force the comparison handle to use the dark style on very light backgrounds */
    shouldShowBlackHandle?: boolean;
    /** Background colour override for clearCut images — forwarded to the comparison curtain */
    backgroundColor?: string;
    /** Fired with `true` when the parent should pause autoplay (hover, upscaling, comparing) */
    onAutoplayPause?: (paused: boolean) => void;
    /** When true, skip all boost logic and render children only */
    disabled?: boolean;
    /** Position of the boost button tooltip */
    tooltipPosition?: ComponentPropsWithoutRef<
        typeof TooltipPrimitive.Content
    >['side'];
}

/**
 * Wrapper component that adds "Boost Resolution" hover button and upscale workflow
 * to any image container. Shows loading overlay during upscale, then a before/after
 * comparison curtain with Keep/Revert actions.
 */
// eslint-disable-next-line import/no-unused-modules
export const BoostResolutionOverlay = ({
    libraryImageId,
    currentImageUrl,
    displayWidth,
    displayHeight,
    expectedWidth,
    expectedHeight,
    columnId,
    onKeepNewImage,
    onRevert,
    wasUpscaled = false,
    children,
    className,
    buttonClassName,
    actionSlot,
    beforeClassName,
    afterClassName,
    actionsClassName,
    multiplied = false,
    shouldShowBlackHandle = false,
    backgroundColor,
    onAutoplayPause,
    disabled = false,
    tooltipPosition,
}: BoostResolutionOverlayProps) => {
    const {
        boostState,
        handleBoostClick,
        handleKeepNewImage,
        handleRevertToOldImage,
        handleViewComparison,
        originalImageUrl,
        upscaledImageUrl,
        error,
        canBoost,
        revertOverrides,
        canRevert,
    } = useExistImageBoost({
        libraryImageId,
        currentImageUrl,
        expectedWidth: expectedWidth ?? displayWidth,
        expectedHeight: expectedHeight ?? displayHeight,
        columnId,
        onKeepNewImage,
        onRevert,
    });

    React.useEffect(() => {
        if (error) {
            toast.error(error);
        }
    }, [error]);

    const AUTO_CLOSE_SEC = 30;
    const isComparing = boostState === 'comparing';
    const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(
        null,
    );
    const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    useEffect(() => {
        if (isComparing) {
            setAutoCloseCountdown(AUTO_CLOSE_SEC);
        } else {
            setAutoCloseCountdown(null);
        }
    }, [isComparing]);

    useEffect(() => {
        if (
            autoCloseCountdown !== null &&
            autoCloseCountdown > 0 &&
            isComparing
        ) {
            countdownTimerRef.current = setTimeout(() => {
                setAutoCloseCountdown((prev) =>
                    prev === null ? null : prev <= 1 ? 0 : prev - 1,
                );
            }, 1000);
        } else if (autoCloseCountdown === 0) {
            handleKeepNewImage();
            setAutoCloseCountdown(null);
        }

        return () => {
            if (countdownTimerRef.current) {
                clearTimeout(countdownTimerRef.current);
                countdownTimerRef.current = null;
            }
        };
    }, [autoCloseCountdown, isComparing, handleKeepNewImage]);

    const handleSliderInteraction = useCallback(() => {
        setAutoCloseCountdown(null);
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const [childImageLoaded, setChildImageLoaded] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const img = container.querySelector('img');
        if (!img) return;
        if (img.complete) {
            setChildImageLoaded(true);
            return;
        }
        const onLoad = () => setChildImageLoaded(true);
        img.addEventListener('load', onLoad);
        return () => img.removeEventListener('load', onLoad);
    }, []);

    useEffect(() => {
        if (!onAutoplayPause) return;
        const shouldPause =
            boostState === 'upscaling' ||
            boostState === 'comparing' ||
            boostState === 'fetching-comparison';
        onAutoplayPause(shouldPause);
    }, [boostState, onAutoplayPause]);

    // Tracks that the boost button was just clicked but the boostState update
    // hasn't propagated yet. Prevents handleButtonMouseLeave from resuming
    // autoplay in the gap between click and re-render.
    const boostStartedRef = useRef(false);

    useEffect(() => {
        if (boostState !== 'idle') {
            boostStartedRef.current = false;
        }
    }, [boostState]);

    const handleButtonMouseEnter = useCallback(() => {
        onAutoplayPause?.(true);
    }, [onAutoplayPause]);

    const handleButtonMouseLeave = useCallback(() => {
        if (boostState === 'idle' && !boostStartedRef.current) {
            onAutoplayPause?.(false);
        }
    }, [boostState, onAutoplayPause]);

    if (disabled) {
        return <>{children}</>;
    }

    if (!canBoost) {
        if (actionSlot) {
            return (
                <div className={cn('group relative', className)}>
                    {children}
                    <div
                        className={cn(hoverActionBarClassName, buttonClassName)}
                    >
                        {actionSlot}
                    </div>
                </div>
            );
        }
        return <>{children}</>;
    }

    const showActionBar =
        childImageLoaded && (boostState === 'idle' || !!actionSlot);
    const effectiveWasUpscaled = revertOverrides ? false : wasUpscaled;
    const isOverlayActive =
        boostState === 'upscaling' ||
        boostState === 'comparing' ||
        boostState === 'fetching-comparison';

    return (
        <div
            ref={containerRef}
            className={cn(
                'group relative',
                className,
                isOverlayActive && 'z-50! overflow-hidden',
            )}
            data-upscaling={boostState === 'upscaling' || undefined}
        >
            {children}

            {/* Optimistic overlay: immediately shows the original image after revert */}
            {revertOverrides && (
                <div
                    className="absolute inset-0 z-5 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url(${revertOverrides.imageUrl})`,
                    }}
                />
            )}

            {showActionBar && (
                <div
                    className={cn(hoverActionBarClassName, buttonClassName)}
                    onMouseEnter={handleButtonMouseEnter}
                    onMouseLeave={handleButtonMouseLeave}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {boostState === 'idle' && (
                        <BoostButton
                            wasUpscaled={effectiveWasUpscaled}
                            tooltipPosition={tooltipPosition}
                            onClick={() => {
                                boostStartedRef.current = true;
                                if (effectiveWasUpscaled) {
                                    handleViewComparison();
                                } else {
                                    handleBoostClick();
                                }
                            }}
                        />
                    )}
                    {actionSlot}
                </div>
            )}

            {(boostState === 'upscaling' ||
                boostState === 'fetching-comparison') && (
                <BoostLoadingOverlay
                    variant={
                        boostState === 'fetching-comparison'
                            ? 'comparison'
                            : 'upscale'
                    }
                />
            )}

            {originalImageUrl && upscaledImageUrl && (
                <ImageComparisonCurtain
                    originalImageUrl={originalImageUrl}
                    upscaledImageUrl={upscaledImageUrl}
                    scrollPosX={50}
                    scrollPosY={50}
                    zoomFactor={100}
                    multiplied={multiplied}
                    shouldShowBlackHandle={shouldShowBlackHandle}
                    backgroundColor={backgroundColor}
                    onClose={handleKeepNewImage}
                    visible={isComparing}
                    showKeepRevertActions
                    onKeepNewImage={handleKeepNewImage}
                    onRevertToOldImage={handleRevertToOldImage}
                    canRevert={canRevert}
                    isActionInProgress={false}
                    autoCloseCountdown={autoCloseCountdown}
                    onSliderInteraction={handleSliderInteraction}
                    beforeClassName={beforeClassName}
                    afterClassName={afterClassName}
                    actionsClassName={actionsClassName}
                    beforeLabel="Original"
                    afterLabel="New Image"
                />
            )}
        </div>
    );
};
